import React, { useContext, useState } from "react";
import "./BookConsultant.css";
import { FaTimes, FaLocationArrow } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarCheck } from "@fortawesome/free-solid-svg-icons";
import { ThemeContext } from "./context/themeContext";

const INVOICE_PRICE_PAISE = 19900;
const CURRENCY_SYMBOL = "\u20B9";

export default function BookConsultant({ setShowPopup, isOpen: propIsOpen, setIsOpen: propSetIsOpen, hideButton }) {
  const [localIsOpen, setLocalIsOpen] = useState(false);
  const isOpen = propIsOpen !== undefined ? propIsOpen : localIsOpen;
  const setIsOpen = propSetIsOpen !== undefined ? propSetIsOpen : setLocalIsOpen;
  const [loading, setLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    date: "",
    time: "",
    topic: "",
    mode: "",
    notes: "",
  });

  const { activeTheme } = useContext(ThemeContext);

  const API_ROOT = process.env.REACT_APP_BACKEND_URL || "https://backend-ewrn.onrender.com";
  const BASE_URL = `${API_ROOT}/book`;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      address: "",
      date: "",
      time: "",
      topic: "",
      mode: "",
      notes: "",
    });
  };

  const handlePayment = async () => {
    setLoading(true);

    try {
      const orderResp = await fetch(`${BASE_URL}/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ amount: INVOICE_PRICE_PAISE }),
      });

      if (!orderResp.ok) throw new Error("Order create failed");

      const data = await orderResp.json();
      const { order, key_id } = data;

      if (!window.Razorpay) {
        await new Promise((resolve) => {
          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          script.onload = resolve;
          document.body.appendChild(script);
        });
      }

      const options = {
        key: key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Marsmate AI",
        description: "Consultant Booking",
        image: "https://i.postimg.cc/mr6Mdf1P/Payment-logo-(1).png",
        order_id: order.id,

        handler: async function (response) {
          const bookingData = { ...formData };
          setIsVerifying(true);
          try {
            const verifyResp = await fetch(`${BASE_URL}/verify-payment`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                amount: INVOICE_PRICE_PAISE,
                ...bookingData,
              }),
            });
//
            const verifyData = await verifyResp.json();

            if (verifyResp.ok && verifyData.success) {
              const emailSent = Boolean(verifyData.email_sent);

              setIsVerifying(false);

              setShowPopup({
                visible: true,
                message: (
                  <div style={{ textAlign: "center" }}>
                    <span style={{ fontSize: "16px", color: "#333" }}>
                      Consultation Booked Successfully!
                    </span>
                    <br />
                    <span
                      style={{
                        color: "#2563eb",
                        fontWeight: "bold",
                        fontSize: "15px",
                        display: "block",
                        marginTop: "5px",
                      }}
                    >
                      {emailSent
                        ? "Invoice sent to your email."
                        : "Booking saved. Invoice email failed."}
                    </span>
                  </div>
                ),
                onClose: () => {
                  setIsOpen(false);
                  resetForm();
                },
              });
            } else {
              setIsVerifying(false);
              setShowPopup({
                visible: true,
                message: "Payment successful but verification failed. Contact support.",
                onClose: () => setIsOpen(false),
              });
            }
          } catch (error) {
            console.error("Verification/PDF Error:", error);
            setIsVerifying(false);
            setShowPopup({
              visible: true,
              message: "Payment successful but invoice email failed.",
              onClose: () => setIsOpen(false),
            });
          }
        },

        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone,
        },
        theme: {
          color: "#ed5d1a",
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function () {
        setShowPopup({
          visible: true,
          message: "Payment Failed. Please try again.",
          onClose: () => setIsOpen(false),
        });
      });

      rzp.open();
    } catch (err) {
      console.error("Payment Start Error:", err);
      setShowPopup({
        visible: true,
        message: "Could not start payment. Check connection.",
        onClose: () => setIsOpen(false),
      });
    } finally {
      setLoading(false);
    }
  };

  const detectLocation = () => {
    if (!navigator.geolocation) return;
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const res = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${coords.latitude}&longitude=${coords.longitude}&localityLanguage=en`
          );
          const data = await res.json();
          const parts = [
            data.locality,
            data.principalSubdivision,
            data.countryName,
            data.postcode,
          ].filter(Boolean);
          setFormData((s) => ({ ...s, address: parts.join(", ") }));
        } catch {
          // silently fail — user can type address manually
        } finally {
          setGeoLoading(false);
        }
      },
      () => setGeoLoading(false),
      { timeout: 8000 }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await handlePayment();
  };

  return (
    <>
      {!hideButton && !isOpen && (
        <button className={`book-btn ${activeTheme === "dark" ? "dark-mode" : ""}`} onClick={() => setIsOpen(true)}>
          <FontAwesomeIcon icon={faCalendarCheck} /> Book Consultant
        </button>
      )}

      {isVerifying && (
        <div className="loading-overlay">
          <div className="bouncing-loader">
            <div></div>
            <div></div>
            <div></div>
          </div>
          <p className="loading-text">Confirming Payment...</p>
        </div>
      )}

      {isOpen && (
        <div
          className={`book-btn-overlay ${activeTheme === "dark" ? "dark-mode" : ""}`}
          onClick={(ev) => {
            if (!isVerifying && ev.target === ev.currentTarget) setIsOpen(false);
          }}
        >
          <div className="book-btn-drawer">
            <div className="book-btn-header">
              <h2>Book a Consultant</h2>
              <button
                className="modal-close"
                onClick={() => !isVerifying && setIsOpen(false)}
                disabled={isVerifying}
                style={{ opacity: isVerifying ? 0.5 : 1, cursor: isVerifying ? "not-allowed" : "pointer" }}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="consult-form">
              <input name="name" placeholder="Full Name" value={formData.name} onChange={handleChange} required disabled={isVerifying} />
              <input name="email" type="email" placeholder="Email" value={formData.email} onChange={handleChange} required disabled={isVerifying} />
              <input name="phone" type="tel" placeholder="Phone" value={formData.phone} onChange={handleChange} required disabled={isVerifying} />
              <div className="address-row">
                <input name="address" placeholder="Address" value={formData.address} onChange={handleChange} required disabled={isVerifying} className="address-input" />
                <button type="button" className="geo-btn" onClick={detectLocation} disabled={isVerifying || geoLoading} title="Auto-detect location">
                  {geoLoading ? <span className="geo-spinner" /> : <FaLocationArrow size={14} />}
                </button>
              </div>

              {/* Date input — wrapper adds custom calendar icon on mobile */}
              <div className="dt-wrapper">
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  disabled={isVerifying}
                  className="dt-input"
                />
                <span className="dt-icon dt-icon-date" aria-hidden="true">
                  {/* Calendar SVG */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </span>
              </div>

              {/* Time input — wrapper adds custom clock icon on mobile */}
              <div className="dt-wrapper">
                <input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                  disabled={isVerifying}
                  className="dt-input"
                />
                <span className="dt-icon dt-icon-time" aria-hidden="true">
                  {/* Clock SVG */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                </span>
              </div>

              <input name="topic" placeholder="Topic" value={formData.topic} onChange={handleChange} required disabled={isVerifying} />

              <select name="mode" value={formData.mode} onChange={handleChange} required disabled={isVerifying}>
                <option value="">Select Mode</option>
                <option value="online">Online</option>
                <option value="in-person">In-Person</option>
              </select>

              <textarea name="notes" placeholder="Notes (Optional)" value={formData.notes} onChange={handleChange} disabled={isVerifying}></textarea>

              <button disabled={loading || isVerifying} className="submit-btn">
                {loading ? "Processing..." : `Pay ${CURRENCY_SYMBOL}199 & Book`}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}